import React, { useEffect, useRef, useState } from 'react';
import { Dropdown, Flex, MenuProps, notification, Space, Tooltip } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { faAngleDown, faArrowRightFromFile } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTime } from 'luxon';
import { useParams } from 'react-router-dom';

import { trackEvent } from 'jf/analytics/Analytics';
import { MSTeamsUser, StudyClient } from 'jf/api';
import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { DevExButton } from 'jf/components/DevExButton';
import { DevExCheckbox } from 'jf/components/DevExCheckbox';
import { exportCSV } from 'jf/pages/studyAnalyzer/export/ExportStudyModal';
import { useLocalStorage } from 'jf/utils/useBrowserStorage';
import { useClientQuery } from 'jf/utils/useClientQuery';
import { useLivePropState } from 'jf/utils/useLivePropState';

import { useStudyBuilderState } from '../../StudyBuilderState';

import { MSTeamsRosterTargetDrawer } from './MSTeamsRosterTargetDrawer';
import { MSTeamsTargetDrawer } from './MSTeamsTargetDrawer';
import { MSTeamsTargetList } from './MSTeamsTargetList';
import { useStudyMSTeamsTargetUpdater } from './useStudyMSTeamsTargetUpdater';

export const MSTeamsTargetEditor: React.FC = (props) => {
  const theme = useDevExTheme();
  const state = useStudyBuilderState();
  const initialMsteamsUsersSet = useRef(false);
  const [msteamsUsers, setMsteamsUsers] = useLivePropState(state.msteamsUsers, (msteamsUsers) =>
    state.update({ msteamsUsers })
  );

  const [msteamsDrawerOpen, setMsteamsDrawerOpen] = useState(false);
  const [showWarning, setShowWarning] = useLocalStorage('showWarning', false);

  const { studyRef: studyRefParam } = useParams<{ studyRef: string }>();
  const { update: saveMsteamsTargets, isUpdating: msteamsTargetsUpdating } =
    useStudyMSTeamsTargetUpdater(studyRefParam);
  const { data: study } = useClientQuery(StudyClient.getStudy, { studyRef: studyRefParam });
  const [duplicates, setDuplicates] = useState(0);
  const [rosterDrawerOpen, setRosterDrawerOpen] = useState(false);

  useEffect(() => {
    if (state.msteamsUsersLoaded) {
      if (initialMsteamsUsersSet.current) {
        // save msteams targets on change, but not on first render
        saveMsteamsTargets(msteamsUsers);
      } else {
        initialMsteamsUsersSet.current = true;
      }
    }
  }, [msteamsUsers]);

  const exportToCSV = (targets: MSTeamsUser[]) => {
    if (!targets || targets.length === 0) {
      return;
    }
    const headerLabels = ['Name', 'Email', 'Username'];
    const selectedKeys = ['name', 'email', 'userName'];
    const headers = headerLabels.join(',');
    const csvData = [[headers]];

    for (const target of targets) {
      const dataRow = [selectedKeys.map((h) => target[h]).join(',')];
      csvData.push(dataRow);
    }
    const fileNameParts = [
      study!.name,
      'Respondents',
      DateTime.now().toLocaleString(DateTime.DATE_SHORT).replaceAll('/', '-'),
    ];
    exportCSV(csvData, fileNameParts);
  };
  const [api, contextHolder] = notification.useNotification();

  const openNotification = () => {
    !showWarning &&
      !msteamsDrawerOpen &&
      api.info({
        message: (
          <Flex vertical gap={theme.variable.spacing.md}>
            {duplicates} {duplicates === 1 ? 'person was' : 'people were'} not added because they
            were already included in your list
            <Flex gap={theme.variable.spacing.sm}>
              <DevExCheckbox onChange={() => setShowWarning(!showWarning)} /> Don't warn me again
            </Flex>
          </Flex>
        ),
        placement: 'topRight',
        duration: 0,
        onClose: () => notification.destroy(),
      });
  };

  useEffect(() => {
    if (duplicates > 0) {
      openNotification();
    }
    setDuplicates(0);
  }, [duplicates, msteamsUsers]);

  const items: MenuProps['items'] = [
    {
      label: 'Add people from your Jellyfish roster',
      key: '1',
    },
    {
      label: 'Add MS Teams users',
      key: '2',
    },
  ];

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    if (e.key === '1') {
      setRosterDrawerOpen(true);
      trackEvent('survey-editor:msteams-target-editor:add-roster');
    } else {
      setMsteamsDrawerOpen(true);
      trackEvent('survey-editor:msteams-target-editor:add-msteams');
    }
  };

  const menuProps = {
    items,
    onClick: handleMenuClick,
  };

  return (
    <Flex vertical gap={theme.variable.spacing.md}>
      {contextHolder}
      <Flex vertical align="center" gap={theme.variable.spacing.sm}>
        <MSTeamsTargetList
          users={msteamsUsers}
          onUpdate={setMsteamsUsers}
          loading={!state.msteamsUsersLoaded}
          saving={msteamsTargetsUpdating}
          loadingText="Loading MS Teams targets..."
          usersLabel={
            <strong>
              {msteamsUsers.length} recipient{msteamsUsers.length === 1 ? '' : 's'}
            </strong>
          }
          trackEventKey="msteams-target-editor"
          extra={[
            <Tooltip
              key="export-csv"
              title={
                msteamsUsers.length === 0
                  ? 'No respondents to export'
                  : 'Export the list of respondents to a CSV'
              }
            >
              <DevExButton
                size="default"
                type="outline"
                icon={<FontAwesomeIcon icon={faArrowRightFromFile} />}
                onClick={() => {
                  trackEvent('survey-editor:msteams-target-editor:export-csv');
                  exportToCSV(msteamsUsers);
                }}
                disabled={msteamsUsers.length === 0}
              />
            </Tooltip>,
            <Dropdown key="open-drawer" menu={menuProps}>
              <DevExButton type="outline">
                <Space>
                  Add
                  <FontAwesomeIcon icon={faAngleDown} color={theme.color.text.secondary} />
                </Space>
              </DevExButton>
            </Dropdown>,
          ]}
          emptyStateCTA={
            <Dropdown menu={menuProps}>
              <DevExButton type="outline">
                <Space>
                  Add
                  <DownOutlined />
                </Space>
              </DevExButton>
            </Dropdown>
          }
          emptyText="Start adding MS Teams users by selecting them from your Jellyfish roster or directly from MS Teams"
        />
      </Flex>
      <MSTeamsRosterTargetDrawer
        onClose={() => setRosterDrawerOpen(false)}
        open={rosterDrawerOpen}
        addUsers={(users) => {
          setMsteamsUsers((oldUsers) => {
            const typecastUsers = users.map((u) => u.msteams!).filter((u) => !!u);
            return [
              ...oldUsers,
              // filter out duplicates before adding
              ...typecastUsers.filter(
                (newTarget) => !oldUsers.some((target) => target.id === newTarget.id)
              ),
            ];
          });
        }}
        msteamsUsers={msteamsUsers}
      />
      <MSTeamsTargetDrawer
        onClose={() => setMsteamsDrawerOpen(false)}
        open={msteamsDrawerOpen}
        onDuplicateChange={setDuplicates}
        msteamsUsers={msteamsUsers}
        addUsers={(users) => {
          setMsteamsUsers((oldUsers) => {
            return [...oldUsers, ...users]; // pseudocode
          });
        }}
      />
    </Flex>
  );
};
